/* global runtime */
// Privatøkonomi: import, kontoer, eiendeler, gjeld og planlegging.
// Lastes som klassisk script før backend.jsx og deler dens bindingsmiljø.
const uploadPersonalFinanceImportFile = async ({ businessId = runtime.activeBusinessId, file } = {}) => {
  await initFirebase();
  if (!runtime.storage) throw new Error(t('svc.noStorage'));
  if (!businessId) throw new Error(t('svc.missingPersonalClient'));
  assertPersonalFinanceImportFile(file);
  const id = createId('personal-finance-import');
  const cleanFileName = safeFilePart(file.name || 'kontoutskrift');
  const hash = await hashFileSha256(file).catch(() => '');
  const storagePath = `businesses/${businessId}/personalFinance/imports/${id}/${Date.now()}-${cleanFileName}`;
  const ref = runtime.storage.ref().child(storagePath);
  await ref.put(file, {
    contentType: file.type || 'application/octet-stream',
    contentDisposition: `inline; filename*=UTF-8''${encodeURIComponent(file.name || cleanFileName)}`,
    customMetadata: {
      businessId,
      originalName: file.name || cleanFileName,
      source: 'plumme-private-economy',
    },
  });
  const downloadUrl = await ref.getDownloadURL().catch(() => '');
  return {
    id,
    name: file.name || cleanFileName,
    mimeType: file.type || 'application/octet-stream',
    size: file.size || 0,
    storagePath,
    downloadUrl: downloadUrl || undefined,
    hash: hash || undefined,
    uploadedAt: nowIso(),
  };
};
// Readmodellen fra privatokonomi-backend inneholder mer enn listene under:
// planning (budsjett), tax, insights, focus, recommendations, savings og trends30.
// Tidligere plukket hver mapper ut en hviteliste og kastet resten, saa budsjettet
// forsvant ved hver sidelasting selv om det laa lagret. Spre payload, normaliser
// bare formen paa feltene som maa vaere lister/objekter.
const personalFinanceReadModel = (payload = {}, extra = {}) => ({
  ...payload,
  accounts: safeArray(payload.accounts),
  assets: safeArray(payload.assets),
  categories: safeArray(payload.categories),
  debts: safeArray(payload.debts),
  debtCategories: safeArray(payload.debtCategories),
  health: payload.health || null,
  jobs: safeArray(payload.jobs),
  monthly: safeArray(payload.monthly),
  summary: payload.summary || {},
  transactions: safeArray(payload.transactions),
  typeBreakdown: safeArray(payload.typeBreakdown),
  ...extra,
});

const getPersonalFinanceOverview = async ({ businessId = runtime.activeBusinessId, year = runtime.activeYear } = {}) => {
  if (!businessId) throw new Error(t('svc.missingPersonalClient'));
  const payload = await authorizedPost('getPersonalFinanceOverviewHttp', { businessId, year });
  return personalFinanceReadModel(payload);
};

const createPersonalFinanceImportJob = async ({ businessId = runtime.activeBusinessId, file, account } = {}) => {
  if (!businessId) throw new Error(t('svc.missingPersonalClient'));
  const payload = await authorizedPost('createPersonalFinanceImportJobHttp', { businessId, file, account });
  return personalFinanceReadModel(payload, {
    job: payload.job || null,
  });
};

const deletePersonalFinanceImportJob = async ({ businessId = runtime.activeBusinessId, jobId, year = runtime.activeYear } = {}) => {
  if (!businessId) throw new Error(t('svc.missingPersonalClient'));
  if (!jobId) throw new Error(t('svc.missingImportJob'));
  const payload = await authorizedPost('deletePersonalFinanceImportJobHttp', { businessId, jobId, year });
  return personalFinanceReadModel(payload, {
    deletedJobId: payload.deletedJobId || jobId,
    deletedTransactions: Number(payload.deletedTransactions || 0),
  });
};

const updatePersonalFinanceTransactionCategory = async ({
  businessId = runtime.activeBusinessId,
  category,
  learn = true,
  transactionIds = [],
  type = 'expense',
  year = runtime.activeYear,
} = {}) => {
  if (!businessId) throw new Error(t('svc.missingPersonalClient2'));
  if (!category) throw new Error(t('svc.pickCategory'));
  if (!transactionIds.length) throw new Error(t('svc.pickTransaction'));
  const payload = await authorizedPost('updatePersonalFinanceTransactionCategoryHttp', {
    businessId,
    category,
    learn,
    transactionIds,
    type,
    year,
  });
  return personalFinanceReadModel(payload, {
    learnedRules: Number(payload.learnedRules || 0),
    movedTransactions: Number(payload.movedTransactions || 0),
  });
};

const uploadPersonalFinanceBankStatement = async ({ businessId = runtime.activeBusinessId, file, account } = {}) => {
  const uploadedFile = await uploadPersonalFinanceImportFile({ businessId, file });
  return createPersonalFinanceImportJob({ businessId, file: uploadedFile, account });
};

const upsertPersonalFinanceAccount = async ({ businessId = runtime.activeBusinessId, account, year = runtime.activeYear } = {}) => {
  if (!businessId) throw new Error(t('svc.missingPersonalClient'));
  if (!account) throw new Error(t('svc.missingAccount'));
  const payload = await authorizedPost('upsertPersonalFinanceAccountHttp', { businessId, account, year });
  return personalFinanceReadModel(payload, {
    account: payload.account || null,
  });
};

const deletePersonalFinanceAccount = async ({ businessId = runtime.activeBusinessId, accountId, year = runtime.activeYear } = {}) => {
  if (!businessId) throw new Error(t('svc.missingPersonalClient'));
  if (!accountId) throw new Error(t('svc.missingAccount'));
  const payload = await authorizedPost('deletePersonalFinanceAccountHttp', { businessId, accountId, year });
  return personalFinanceReadModel(payload, {
    deletedAccountId: payload.deletedAccountId || accountId,
    deletedStatements: Number(payload.deletedStatements || 0),
    deletedTransactions: Number(payload.deletedTransactions || 0),
  });
};

const recategorizePersonalFinanceTransactions = async ({ businessId = runtime.activeBusinessId, year = runtime.activeYear } = {}) => {
  if (!businessId) throw new Error(t('svc.missingPersonalClient'));
  const payload = await authorizedPost('recategorizePersonalFinanceTransactionsHttp', { businessId, year });
  return personalFinanceReadModel(payload, {
    manualTransactions: Number(payload.manualTransactions || 0),
    recategorizedTransactions: Number(payload.recategorizedTransactions || 0),
    scannedTransactions: Number(payload.scannedTransactions || 0),
    unchangedTransactions: Number(payload.unchangedTransactions || 0),
    uncategorizedTransactions: Number(payload.uncategorizedTransactions || 0),
  });
};

const linkPersonalFinanceImportJobAccount = async ({ businessId = runtime.activeBusinessId, jobId, account, year = runtime.activeYear } = {}) => {
  if (!businessId) throw new Error(t('svc.missingPersonalClient'));
  if (!jobId) throw new Error(t('svc.missingStatement'));
  if (!account) throw new Error(t('svc.missingAccount'));
  const payload = await authorizedPost('linkPersonalFinanceImportJobAccountHttp', { businessId, jobId, account, year });
  return personalFinanceReadModel(payload, {
    account: payload.account || null,
    linkedJobId: payload.linkedJobId || jobId,
    updatedTransactions: Number(payload.updatedTransactions || 0),
  });
};

const upsertPersonalFinanceAsset = async ({ businessId = runtime.activeBusinessId, asset, year = runtime.activeYear } = {}) => {
  if (!businessId) throw new Error(t('svc.missingPersonalClient'));
  if (!asset) throw new Error(t('svc.missingAsset'));
  const payload = await authorizedPost('upsertPersonalFinanceAssetHttp', { businessId, asset, year });
  return personalFinanceReadModel(payload, {
    asset: payload.asset || null,
  });
};

const deletePersonalFinanceAsset = async ({ businessId = runtime.activeBusinessId, assetId, year = runtime.activeYear } = {}) => {
  if (!businessId) throw new Error(t('svc.missingPersonalClient'));
  if (!assetId) throw new Error(t('svc.missingAsset'));
  const payload = await authorizedPost('deletePersonalFinanceAssetHttp', { businessId, assetId, year });
  return personalFinanceReadModel(payload, {
    deletedAssetId: payload.deletedAssetId || assetId,
  });
};

const upsertPersonalFinanceLoan = async ({ businessId = runtime.activeBusinessId, loan, year = runtime.activeYear } = {}) => {
  if (!businessId) throw new Error(t('svc.missingPersonalClient'));
  if (!loan) throw new Error(t('svc.missingLoan'));
  const payload = await authorizedPost('upsertPersonalFinanceLoanHttp', { businessId, loan, year });
  return personalFinanceReadModel(payload, {
    loan: payload.loan || null,
  });
};

const deletePersonalFinanceLoan = async ({ businessId = runtime.activeBusinessId, loanId, year = runtime.activeYear } = {}) => {
  if (!businessId) throw new Error(t('svc.missingPersonalClient'));
  if (!loanId) throw new Error(t('svc.missingLoan'));
  const payload = await authorizedPost('deletePersonalFinanceLoanHttp', { businessId, loanId, year });
  return personalFinanceReadModel(payload, {
    deletedLoanId: payload.deletedLoanId || loanId,
  });
};

const updatePersonalFinancePlanning = async ({ businessId = runtime.activeBusinessId, patch, year = runtime.activeYear } = {}) => {
  if (!businessId) throw new Error(t('svc.pickPersonalClient'));
  if (!patch || typeof patch !== 'object' || Array.isArray(patch)) throw new Error(t('svc.missingPersonalData'));
  const payload = await authorizedPost('updatePersonalFinancePlanningHttp', { businessId, patch, year });
  if (!payload || typeof payload !== 'object') throw new Error(t('svc.invalidPersonalSave'));
  return payload;
};
